// Categorize banks — the data behind the `categorize` exercise ("sort each item
// into its one bucket"). Every item carries exactly one correct bucketId, so
// grading is unambiguous. These drill classification skills the lessons teach
// directly (vowel length, the five vargas, grammatical gender, stem class) —
// none require reading a word, so they honestly fill the early lessons that the
// word-band drills could not (Reading L2/L3, Grammar L2).

import type { CategorizeBank } from './types';

// --- reading: the thirteen vowels by length (Reading L2) ---------------------
// Short: a i u ṛ ḷ. Long: ā ī ū ṝ, and the diphthong class e ai o au (all
// phonologically long — there is no short e/o in classical Sanskrit).
const VOWEL_LENGTH: CategorizeBank = {
  buckets: [
    { id: 'short', label: 'Short' },
    { id: 'long', label: 'Long' },
  ],
  items: [
    { id: 'a', deva: 'अ', iast: 'a', bucketId: 'short', gloss: 'short a (the u in “but”)' },
    { id: 'aa', deva: 'आ', iast: 'ā', bucketId: 'long', gloss: 'long ā (the a in “father”)' },
    { id: 'i', deva: 'इ', iast: 'i', bucketId: 'short', gloss: 'short i (the i in “bit”)' },
    { id: 'ii', deva: 'ई', iast: 'ī', bucketId: 'long', gloss: 'long ī (the ee in “machine”)' },
    { id: 'u', deva: 'उ', iast: 'u', bucketId: 'short', gloss: 'short u (the u in “put”)' },
    { id: 'uu', deva: 'ऊ', iast: 'ū', bucketId: 'long', gloss: 'long ū (the oo in “rule”)' },
    { id: 'r', deva: 'ऋ', iast: 'ṛ', bucketId: 'short', gloss: 'short vocalic r' },
    { id: 'rr', deva: 'ॠ', iast: 'ṝ', bucketId: 'long', gloss: 'long vocalic r' },
    { id: 'l', deva: 'ऌ', iast: 'ḷ', bucketId: 'short', gloss: 'short vocalic l' },
    { id: 'e', deva: 'ए', iast: 'e', bucketId: 'long', gloss: 'e — diphthong class, long' },
    { id: 'ai', deva: 'ऐ', iast: 'ai', bucketId: 'long', gloss: 'ai — diphthong class, long' },
    { id: 'o', deva: 'ओ', iast: 'o', bucketId: 'long', gloss: 'o — diphthong class, long' },
    { id: 'au', deva: 'औ', iast: 'au', bucketId: 'long', gloss: 'au — diphthong class, long' },
  ],
};

// --- reading: the 25 grid consonants by varga / place (Reading L3) -----------
const CONSONANT_VARGA: CategorizeBank = {
  buckets: [
    { id: 'velar', label: 'Velar (kaṇṭhya)' },
    { id: 'palatal', label: 'Palatal (tālavya)' },
    { id: 'retroflex', label: 'Retroflex (mūrdhanya)' },
    { id: 'dental', label: 'Dental (dantya)' },
    { id: 'labial', label: 'Labial (oṣṭhya)' },
  ],
  items: [
    { id: 'ka', deva: 'क', iast: 'ka', bucketId: 'velar' },
    { id: 'kha', deva: 'ख', iast: 'kha', bucketId: 'velar' },
    { id: 'ga', deva: 'ग', iast: 'ga', bucketId: 'velar' },
    { id: 'gha', deva: 'घ', iast: 'gha', bucketId: 'velar' },
    { id: 'nga', deva: 'ङ', iast: 'ṅa', bucketId: 'velar' },
    { id: 'ca', deva: 'च', iast: 'ca', bucketId: 'palatal' },
    { id: 'cha', deva: 'छ', iast: 'cha', bucketId: 'palatal' },
    { id: 'ja', deva: 'ज', iast: 'ja', bucketId: 'palatal' },
    { id: 'jha', deva: 'झ', iast: 'jha', bucketId: 'palatal' },
    { id: 'nya', deva: 'ञ', iast: 'ña', bucketId: 'palatal' },
    { id: 'tta', deva: 'ट', iast: 'ṭa', bucketId: 'retroflex' },
    { id: 'ttha', deva: 'ठ', iast: 'ṭha', bucketId: 'retroflex' },
    { id: 'dda', deva: 'ड', iast: 'ḍa', bucketId: 'retroflex' },
    { id: 'ddha', deva: 'ढ', iast: 'ḍha', bucketId: 'retroflex' },
    { id: 'nna', deva: 'ण', iast: 'ṇa', bucketId: 'retroflex' },
    { id: 'ta', deva: 'त', iast: 'ta', bucketId: 'dental' },
    { id: 'tha', deva: 'थ', iast: 'tha', bucketId: 'dental' },
    { id: 'da', deva: 'द', iast: 'da', bucketId: 'dental' },
    { id: 'dha', deva: 'ध', iast: 'dha', bucketId: 'dental' },
    { id: 'na', deva: 'न', iast: 'na', bucketId: 'dental' },
    { id: 'pa', deva: 'प', iast: 'pa', bucketId: 'labial' },
    { id: 'pha', deva: 'फ', iast: 'pha', bucketId: 'labial' },
    { id: 'ba', deva: 'ब', iast: 'ba', bucketId: 'labial' },
    { id: 'bha', deva: 'भ', iast: 'bha', bucketId: 'labial' },
    { id: 'ma', deva: 'म', iast: 'ma', bucketId: 'labial' },
  ],
};

// --- reading: the three sibilants by place (Reading L4) ----------------------
const SIBILANT_PLACE: CategorizeBank = {
  buckets: [
    { id: 'palatal', label: 'Palatal' },
    { id: 'retroflex', label: 'Retroflex' },
    { id: 'dental', label: 'Dental' },
  ],
  items: [
    { id: 'sha', deva: 'श', iast: 'śa', bucketId: 'palatal', gloss: 'ś — palatal “sh”, tongue forward' },
    { id: 'ssa', deva: 'ष', iast: 'ṣa', bucketId: 'retroflex', gloss: 'ṣ — retroflex “sh”, tongue curled back' },
    { id: 'sa', deva: 'स', iast: 'sa', bucketId: 'dental', gloss: 's — plain dental “s”' },
  ],
};

// --- grammar: nouns by grammatical gender (Grammar L2) -----------------------
const NOUN_GENDER: CategorizeBank = {
  buckets: [
    { id: 'm', label: 'Masculine' },
    { id: 'n', label: 'Neuter' },
    { id: 'f', label: 'Feminine' },
  ],
  items: [
    { id: 'deva', deva: 'देव', iast: 'deva', bucketId: 'm', gloss: 'god (a-stem masc.)' },
    { id: 'nara', deva: 'नर', iast: 'nara', bucketId: 'm', gloss: 'man (a-stem masc.)' },
    { id: 'grama', deva: 'ग्राम', iast: 'grāma', bucketId: 'm', gloss: 'village (a-stem masc.)' },
    { id: 'hari', deva: 'हरि', iast: 'hari', bucketId: 'm', gloss: 'Viṣṇu (i-stem masc.)' },
    { id: 'agni', deva: 'अग्नि', iast: 'agni', bucketId: 'm', gloss: 'fire (i-stem masc.)' },
    { id: 'guru', deva: 'गुरु', iast: 'guru', bucketId: 'm', gloss: 'teacher (u-stem masc.)' },
    { id: 'phala', deva: 'फल', iast: 'phala', bucketId: 'n', gloss: 'fruit (a-stem neut.)' },
    { id: 'vana', deva: 'वन', iast: 'vana', bucketId: 'n', gloss: 'forest (a-stem neut.)' },
    { id: 'mandira', deva: 'मन्दिर', iast: 'mandira', bucketId: 'n', gloss: 'temple (a-stem neut.)' },
    { id: 'mala', deva: 'माला', iast: 'mālā', bucketId: 'f', gloss: 'garland (ā-stem fem.)' },
    { id: 'sena', deva: 'सेना', iast: 'senā', bucketId: 'f', gloss: 'army (ā-stem fem.)' },
    { id: 'nadi', deva: 'नदी', iast: 'nadī', bucketId: 'f', gloss: 'river (ī-stem fem.)' },
  ],
};

// --- grammar: stems by final sound (Grammar L2) ------------------------------
const STEM_FINAL: CategorizeBank = {
  buckets: [
    { id: 'a', label: 'a-stem (short a)' },
    { id: 'aa', label: 'ā-stem (long ā)' },
    { id: 'i', label: 'i-stem (short i)' },
    { id: 'u', label: 'u-stem (short u)' },
    { id: 'ii', label: 'ī-stem (long ī)' },
  ],
  items: [
    { id: 'deva', deva: 'देव', iast: 'deva', bucketId: 'a', gloss: 'god' },
    { id: 'phala', deva: 'फल', iast: 'phala', bucketId: 'a', gloss: 'fruit' },
    { id: 'vana', deva: 'वन', iast: 'vana', bucketId: 'a', gloss: 'forest' },
    { id: 'mala', deva: 'माला', iast: 'mālā', bucketId: 'aa', gloss: 'garland' },
    { id: 'sena', deva: 'सेना', iast: 'senā', bucketId: 'aa', gloss: 'army' },
    { id: 'hari', deva: 'हरि', iast: 'hari', bucketId: 'i', gloss: 'Viṣṇu' },
    { id: 'agni', deva: 'अग्नि', iast: 'agni', bucketId: 'i', gloss: 'fire' },
    { id: 'guru', deva: 'गुरु', iast: 'guru', bucketId: 'u', gloss: 'teacher' },
    { id: 'nadi', deva: 'नदी', iast: 'nadī', bucketId: 'ii', gloss: 'river' },
  ],
};

const CATEGORIZE_BANKS: Record<string, CategorizeBank> = {
  'vowel-length': VOWEL_LENGTH,
  'consonant-varga': CONSONANT_VARGA,
  'sibilant-place': SIBILANT_PLACE,
  'noun-gender': NOUN_GENDER,
  'stem-final': STEM_FINAL,
};

export function categorizeBank(key: string): CategorizeBank | undefined {
  return CATEGORIZE_BANKS[key];
}
